import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
} from "@mui/material";

import { videoPlaceholderImage } from "../../../constants/ImagePaths";
import fontWeight from "../../../styles/mui/fontWeight";
import {
  ActionIconBox,
  CardActionsStyle,
  FaPenToSquareIcon,
  FaRegCirclePlayIcon,
  FaRegTrashCanIcon,
  LongText,
  PlayButton,
  PlayButtonBox,
  ToggleButton,
  VideoCardMedia,
  VideoCardStyle,
  VideoDescriptionCollapsed,
} from "../styles/style";
import { TrainingVideo, TrainingVideoForm } from "../../../models/Training";
import { colors } from "../../../styles/colors";
import { MyAccountModel } from "../../../models/MyAccount";
import { UserRole } from "../../../constants/HomePage";
import { getValue } from "../../../utils";
import AddEditTraining from "../containers/AddEditTraining";
import { BookmarkDefaultData } from "../../../constants/Training";

export interface PropsFromState {
  trainingVideoData: TrainingVideo;
  handleDeleteVideo: (videoId: number) => void;
  loggedInUser: MyAccountModel;
  handleViewVideo: (video: TrainingVideo) => void;
}

type AllProps = PropsFromState;

const VideoCard: React.FC<AllProps> = ({
  trainingVideoData,
  handleDeleteVideo,
  loggedInUser,
  handleViewVideo,
}) => {
  const [showFullDescription, setShowFullDescription] =
    useState<boolean>(false);
  const [openEditTrainingModal, setOpenEditTrainingModal] =
    useState<boolean>(false);
  const thumbnailUrl = videoPlaceholderImage;
  const [editTrainingVideo, setEditTrainingVideo] = useState<TrainingVideoForm>(
    {
      videoID: 0,
      title: "",
      description: "",
      createdBy: loggedInUser?.providerPortalUser?.email,
      bookmarks: [BookmarkDefaultData],
    }
  );

  const handleToggleButton = () => {
    setShowFullDescription(!showFullDescription);
  };

  const handleEditVideo = (video: TrainingVideo) => {
    setOpenEditTrainingModal(true);

    const bookmarksArray = video.bookmarks
      ? video.bookmarks.split(";").map((bookmark: any) => {
          const [time, description] = bookmark.trim().split(" - ");
          return {
            time: time.replace("-", "").trim(),
            description: description ? description.trim() : "",
          };
        })
      : [];

      const editVideoData = {
      videoID: video.videoID,
      title: video.title,
      description: video.description,
      createdBy: video.createdBy,
      bookmarks: bookmarksArray,
    };

    setEditTrainingVideo(editVideoData);
  };

  return (
    <Card sx={VideoCardStyle}>
      <CardMedia
        component="img"
        image={thumbnailUrl}
        alt={`Thumbnail for ${trainingVideoData.title}`}
        sx={VideoCardMedia}
      />
      <Box sx={PlayButtonBox}>
        <Button
          sx={PlayButton}
          onClick={() => handleViewVideo(trainingVideoData)}
        >
          <FaRegCirclePlayIcon />
        </Button>
      </Box>
      <CardContent>
        <Typography
          variant="h1"
          color={colors.portalColor[25]}
          fontWeight={fontWeight[6]}
          sx={LongText}
        >
          {trainingVideoData.title}
        </Typography>
        <Typography
          variant="h4"
          color={colors.portalColor[26]}
          fontWeight={fontWeight[1]}
          mt={2}
          sx={VideoDescriptionCollapsed(showFullDescription)}
        >
          {trainingVideoData.description}
        </Typography>
        <Button
          sx={ToggleButton}
          disableRipple
          disableFocusRipple
          onClick={handleToggleButton}
        >
          {showFullDescription ? "View Less" : "View More"}
        </Button>
      </CardContent>
      <CardActions disableSpacing sx={CardActionsStyle}>
        <Button
          variant="contained"
          onClick={() => handleViewVideo(trainingVideoData)}
        >
          View Video
        </Button>
        {getValue(loggedInUser, "providerPortalUser.roleType") ===
          UserRole.TANGO_ADMIN && (
          <Box sx={ActionIconBox}>
            <IconButton onClick={() => handleEditVideo(trainingVideoData)}>
              <FaPenToSquareIcon />
            </IconButton>
            <IconButton
              onClick={() => handleDeleteVideo(trainingVideoData.videoID)}
            >
              <FaRegTrashCanIcon />
            </IconButton>
          </Box>
        )}
      </CardActions>
      {openEditTrainingModal && (
        <AddEditTraining
          isOpenModal={openEditTrainingModal}
          setIsOpenModal={setOpenEditTrainingModal}
          isEditVideo={true}
          editTrainingVideo={editTrainingVideo}
          loggedInUser={loggedInUser}
        />
      )}
    </Card>
  );
};

export default VideoCard;
